<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Landmark, ChevronRight } from 'lucide-vue-next'
import { Alert, Badge, Button, Card, Spinner } from '@/components/ui'
import SetupFlowShell from '@/components/shared/SetupFlowShell.vue'
import {
  selectLoginProvider,
  type StoreAuthStatus,
  type StoreProviderOption,
  type StoreSelectionResponse,
} from '@/api/authClient'
import { track } from '@/lib/usageTracker'
import { useAuthStore } from '@/stores/authStore'

// --- Interface-design checkpoint ---
// Intent: provider chooser when a store has multiple active integrations.
// Each option is a provider card with display name, login type, and
// store/invoice provenance chips. Replaces a generic picker.

const router = useRouter()
const store = useAuthStore()

const loading = ref(false)
const apiError = ref(false)
const networkError = ref(false)
const errorMessage = ref('We could not open that provider.')
const pendingProvider = ref<string | null>(null)

const contextItems = computed(() => [
  {
    label: 'Acting context',
    value: store.user?.email ? `Signed in as ${store.user.email}` : 'No user',
    tone: store.user?.email ? 'success' : 'warning',
  },
  {
    label: 'Store',
    value: store.selectedStore?.storeName
      ? `${store.selectedStore.storeName} (${store.selectedStore.storeDb})`
      : 'Not selected',
    tone: store.selectedStore ? 'default' : 'warning',
  },
  {
    label: 'Providers available',
    value: store.providerOptions.length > 0 ? `${store.providerOptions.length}` : 'None',
    tone: store.providerOptions.length > 0 ? 'default' : 'warning',
  },
])

function resetUI() {
  loading.value = false
  apiError.value = false
  networkError.value = false
}

async function choose(option: StoreProviderOption) {
  track('btn:OpenLoginProvider', 'click', { provider: option.provider })
  resetUI()
  if (!store.user || !store.selectedStore) {
    router.replace({ name: 'StoreSelection' })
    return
  }
  pendingProvider.value = option.provider
  try {
    loading.value = true
    const response: StoreSelectionResponse = await selectLoginProvider({
      userId: store.user.id,
      storeId: store.selectedStore.storeId,
      storeDb: store.selectedStore.storeDb,
      provider: option.provider,
    })
    handleResponse(response)
  } catch (e) {
    const msg = String((e as Error)?.message || e)
    if (/failed to fetch|network|TypeError/i.test(msg)) {
      networkError.value = true
    } else {
      apiError.value = true
      errorMessage.value = msg
    }
    console.error(e)
  } finally {
    loading.value = false
    pendingProvider.value = null
  }
}

function handleResponse(response: StoreSelectionResponse) {
  if (!response) return
  const status: StoreAuthStatus = response.status
  if (status === 'READY' && response.apiKey && response.provider) {
    store.selectPackage(response.provider.provider)
    store.setIntegrationResult({
      provider: response.provider.provider,
      apiKey: response.apiKey,
      integrationId: response.integrationId ?? undefined,
      authStatus: 'ready',
    })
    store.setProviderOptions([])
    router.replace({ name: 'Dashboard' })
    return
  }
  if (status === 'AUTH_REQUIRED' && response.provider) {
    store.setProviderAuthRequired(response.provider.provider, response.authRedirectUrl ?? null)
    if (response.authRedirectUrl) {
      window.location.href = response.authRedirectUrl
      return
    }
  }
  // Fallback: bounce to store selection to recover.
  router.replace({ name: 'StoreSelection' })
}

function back() {
  router.replace({ name: 'StoreSelection' })
}

onMounted(() => {
  if (!store.user) {
    router.replace({ name: 'UserLogin' })
    return
  }
  if (!store.selectedStore) {
    router.replace({ name: 'StoreSelection' })
    return
  }
  if (store.providerOptions.length === 0) {
    router.replace({ name: 'StoreSelection' })
  }
})
</script>

<template>
  <SetupFlowShell
    eyebrow="Choose a provider"
    title="Which accounting provider should we open?"
    subtitle="Multiple providers are configured for this store. Each card carries the provider identity and login type so you can confirm before continuing."
    :current-step="3"
    :steps="['Sign in', 'Choose store', 'Connect', 'Workspace']"
    :context-items="contextItems"
  >
    <Card padding="lg" class="provider-panel">
      <div class="provider-panel__actions">
        <Button variant="secondary" id="backToStores" @click="() => { track('btn:BackToStores', 'click'); back() }">Back to stores</Button>
      </div>
      <div class="provider-panel__grid">
        <Card
          v-for="option in store.providerOptions"
          :key="option.provider"
          padding="md"
          class="provider-card"
          :class="{ 'provider-card--pending': pendingProvider === option.provider }"
        >
          <div class="provider-card__row">
            <div class="provider-card__icon" aria-hidden="true">
              <Landmark :size="18" />
            </div>
            <div class="provider-card__body">
              <div class="provider-card__name">{{ option.displayName || option.provider }}</div>
              <div class="provider-card__meta">
                <span>Provider code: {{ option.provider }}</span>
                <span class="provider-card__sep">·</span>
                <span>Login: {{ option.loginType || 'OAuth2' }}</span>
              </div>
              <div class="provider-card__provenance">
                <Badge variant="muted">{{ store.selectedStore?.storeDb ?? 'store' }}</Badge>
                <Badge variant="muted">Group {{ store.selectedStore?.group ?? '-' }}</Badge>
              </div>
            </div>
            <Button
              size="sm"
              variant="primary"
              class="provider-card__action"
              :disabled="loading"
              @click="choose(option)"
            >
              <Spinner v-if="loading && pendingProvider === option.provider" size="sm" />
              <span v-if="!loading || pendingProvider !== option.provider">Open</span>
              <span v-else>Opening</span>
              <ChevronRight v-if="!loading || pendingProvider !== option.provider" :size="14" />
            </Button>
          </div>
        </Card>
      </div>

      <Alert
        v-if="apiError"
        id="providerApiError"
        variant="error"
        title="Provider selection failed"
      >
        {{ errorMessage }}
        <div class="provider-panel__alert-actions">
          <Button id="retryProvider" @click="() => { track('btn:RetryLoginProvider', 'click'); pendingProvider && choose(store.providerOptions.find(o => o.provider === pendingProvider) as StoreProviderOption) }">Try again</Button>
        </div>
      </Alert>

      <Alert
        v-else-if="networkError"
        id="providerNetworkError"
        variant="warning"
        title="Network unreachable"
      >
        We couldn't reach the Lyt Accounting service. Check your connection and retry.
        <div class="provider-panel__alert-actions">
          <Button id="retryProviderNetwork" @click="() => { track('btn:RetryLoginProvider', 'click'); pendingProvider && choose(store.providerOptions.find(o => o.provider === pendingProvider) as StoreProviderOption) }">Retry</Button>
        </div>
      </Alert>
    </Card>
  </SetupFlowShell>
</template>

<style scoped>
.provider-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.provider-panel__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}
.provider-panel__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
}

.provider-card {
  transition: border-color 0.15s ease;
}

.provider-card:hover {
  border-color: var(--color-border-strong);
}

.provider-card--pending {
  border-color: var(--color-primary);
}

.provider-card__row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.provider-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-sm);
  background: var(--color-surface-muted);
  color: var(--color-ink);
}

.provider-card__body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.provider-card__name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-ink);
}

.provider-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
  font-size: 0.82rem;
  color: var(--color-ink-soft);
}

.provider-card__sep {
  color: var(--color-border-strong);
}

.provider-card__provenance {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.provider-card__action {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.provider-panel__alert-actions {
  margin-top: 0.6rem;
}

@media (min-width: 720px) {
  .provider-panel__grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
